import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FaSuitcase, FaPlane, FaBed, FaCar, FaUmbrellaBeach, FaShuttleVan } from 'react-icons/fa';
import { ROUTE_KEYS, SESSION_STORAGE_KEYS } from 'legacyfrontend/constants';
import { useColors } from 'legacyfrontend/hooks/useColors';
import { useGlobal } from 'legacyfrontend/contexts/GlobalContext';
import {
  selectTemplateData,
  selectIsPackageSearcherEnabled,
  selectIsFlightSearcherEnabled,
  // selectIsHotelSearcherEnabled,
  selectHasActiveSearchers,
  selectApplicationId,
  selectToken,
} from 'legacyfrontend/redux/selectors/application-selectors';
import { isClient } from 'legacyfrontend/helpers/ssr-helper';
import aplicacionesApi from 'legacyfrontend/api/aplicaciones';

const FEATURES_CONFIG = [
  {
    id: 'packages',
    label: 'Paquetes',
    icon: FaSuitcase,
    route: ROUTE_KEYS.BUSCAR_X_PAQUETES,
    enabled: true,
    active: true,
  },
  {
    id: 'flights',
    label: 'Vuelos',
    icon: FaPlane,
    route: ROUTE_KEYS.BUSCAR_X_VUELOS,
    enabled: true,
    active: true,
  },
  {
    id: 'hotels',
    label: 'Hoteles',
    icon: FaBed,
    externalLink: '',
    enabled: true,
    active: true,
  },
  {
    id: 'cars',
    label: 'Autos',
    icon: FaCar,
    externalLink: '',
    enabled: true,
    active: true,
  },
  {
    id: 'activities',
    label: 'Actividades',
    icon: FaUmbrellaBeach,
    externalLink: '',
    enabled: true,
    active: true,
  },
  {
    id: 'transfers',
    label: 'Traslados',
    icon: FaShuttleVan,
    externalLink: '',
    enabled: true,
    active: true,
  },
];

const FeatureButton = ({ feature, isActive, onClick }) => {
  const IconComponent = feature.icon;

  const { background } = useColors();

  if (!feature.active) {
    return <></>;
  }

  const buttonClassName = `
    w-full
    flex flex-col items-center justify-center
    px-2 sm:px-3 transition-all duration-200 h-12 
    ${isActive ? 'bg-white' : 'bg-white/70'}
    lg:backdrop-blur-3xl lg:rounded-lg lg:drop-shadow-lg 
    ${isActive ? 'text-white' : 'text-gray-600 hover:text-gray-800'}
    hover:bg-white-50
    active:bg-gray-100/50 lg:active:bg-white/40
    ${!feature.enabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
    hover:transform hover:-translate-y-1
    active:transform active:scale-95 active:translate-y-0
  `;

  const content = (
    <div className='flex flex-row items-center justify-center'>
      <div
        className={`
          w-8 h-8 rounded-full flex items-center justify-center mr-3
          transition-all duration-200
        `}
        style={{
          border: isActive ? `2px solid ${background}` : '2px solid transparent',
        }}
      >
        <IconComponent
          size={20}
          className='text-gray-600'
          style={{
            color: isActive ? background : undefined,
          }}
        />
      </div>

      <span
        className={`text-sm font-medium transition-colors duration-200 text-gray-${isActive ? '800' : '600'}`}
      >
        {feature.label}
      </span>
    </div>
  );

  if (feature.externalLink) {
    return (
      <a
        href={feature.externalLink || '#'}
        onClick={(e) => {
          if (!feature.externalLink || feature.externalLink === '#') {
            e.preventDefault();
          }
          onClick(e);
        }}
        className={buttonClassName}
        target={feature.externalLink && feature.externalLink !== '#' ? '_blank' : undefined}
        rel={feature.externalLink && feature.externalLink !== '#' ? 'noopener noreferrer' : undefined}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      onClick={onClick}
      className={buttonClassName}
      disabled={!feature.enabled}
    >
      {content}
    </button>
  );
};

const FeatureSelector = () => {
  const history = useHistory();
  const location = useLocation();
  const [activeFeature, setActiveFeature] = useState(null);
  const [accionesAplicacion, setAccionesAplicacion] = useState([]);

  const templateData = useSelector(selectTemplateData);
  const { resetOnSearchCount } = useGlobal();

  const isPackageSearcherEnabled = useSelector(selectIsPackageSearcherEnabled);
  const isFlightSearcherEnabled = useSelector(selectIsFlightSearcherEnabled);
  // const isHotelSearcherEnabled = useSelector(selectIsHotelSearcherEnabled);
  const hasActiveSearchers = useSelector(selectHasActiveSearchers);
  const applicationId = useSelector(selectApplicationId);
  const token = useSelector(selectToken);

  const shouldRender = useMemo(() => {
    return hasActiveSearchers;
  }, [hasActiveSearchers]);

  const nameToIdMap = {
    'autos': 'cars',
    'hoteles': 'hotels',
    'actividades': 'activities',
    'traslados': 'transfers',
  };

  const enabledFeatures = useMemo(() => {
    return FEATURES_CONFIG.map((feature) => {
      if (feature.externalLink !== undefined) {
        const accion = accionesAplicacion.find(
          (acc) => nameToIdMap[acc.name] === feature.id
        );

        if (accion) {
          return {
            ...feature,
            externalLink: accion.url || '',
            active: accion.activo === true,
            enabled: accion.activo === true,
          };
        }

        return {
          ...feature,
          active: false,
          enabled: false,
        };
      }

      const isPackage = feature.id === 'packages';
      const isFlight = feature.id === 'flights';
      
      if (isPackage) {
        return {
          ...feature,
          active: isPackageSearcherEnabled,
          enabled: isPackageSearcherEnabled,
        };
      }
      
      if (isFlight) {
        return {
          ...feature,
          active: isFlightSearcherEnabled,
          enabled: isFlightSearcherEnabled,
        };
      }
      
      return {
        ...feature,
        active: feature.active,
        enabled: feature.enabled,
      };
    });
  }, [isPackageSearcherEnabled, isFlightSearcherEnabled, accionesAplicacion]);

  const activeFeatures = useMemo(() => {
    return enabledFeatures.filter((feature) => feature.active);
  }, [enabledFeatures]);

  const clearAllState = useCallback(() => {
    resetOnSearchCount();

    if (isClient) {
      Object.values(SESSION_STORAGE_KEYS).forEach((key) => {
        sessionStorage.removeItem(key);
      });

      sessionStorage.removeItem('flightInfo');
      sessionStorage.removeItem('packageAdvice');

      if ('caches' in window) {
        caches.keys().then((cacheNames) => {
          cacheNames.forEach((cacheName) => {
            if (cacheName.includes('search') || cacheName.includes('api')) {
              caches.delete(cacheName);
            }
          });
        });
      }
    }
  }, [resetOnSearchCount]);

  const handleFeatureClick = async (feature, event) => {
    if (!feature.enabled) return;

    if (feature.externalLink !== undefined) {
      if (feature.externalLink && feature.externalLink !== '#' && feature.externalLink !== '') {
        if (isClient) {
          window.open(feature.externalLink, '_blank', 'noopener,noreferrer');
        }
      }
      return;
    }

    if (event) {
      event.preventDefault();
    }

    if (activeFeature && activeFeature !== feature.id) {
      clearAllState();
    }
    setActiveFeature(feature.id);

    if (feature.route) {
      history.push(feature.route);
    }
  };

  useEffect(() => {
    const fetchAccionesAplicacion = async () => {
      if (applicationId && token) {
        try {
          const response = await aplicacionesApi.getAccionesAplicacion(applicationId, token);
          
          if (Array.isArray(response)) {
            setAccionesAplicacion(response);
          } else if (response && Array.isArray(response.data)) {
            setAccionesAplicacion(response.data);
          } else if (response && Array.isArray(response.object)) {
            setAccionesAplicacion(response.object);
          } else {
            setAccionesAplicacion([]);
          }
        } catch (error) {
          setAccionesAplicacion([]);
        }
      }
    };

    fetchAccionesAplicacion();
  }, [applicationId, token]);

  useEffect(() => {
    const currentPath = location.pathname;
    const activeFeature = activeFeatures.find(
      (feature) => feature.enabled && feature.route && currentPath.includes(feature.route)
    );

    if (activeFeature) {
      setActiveFeature(activeFeature.id);
    } else {
      setActiveFeature(null);
    }
  }, [location.pathname, activeFeatures]);

  if (!shouldRender) {
    return null;
  }

  const hasMoreThanTwoButtons = activeFeatures.length > 2;

  return (
    <>
      <div 
        className={`flex w-full gap-2 flex-row justify-start lg:justify-center feature-selector-container ${hasMoreThanTwoButtons ? 'flex-nowrap scrollbar-hide has-more-than-two' : 'flex-wrap'}`} 
        style={{ 
          minHeight: '48px',
          paddingTop: '4px',
          width: '100%',
          display: 'flex',
          visibility: 'visible',
          opacity: 1,
          overflowY: 'visible'
        }}
      >
        {activeFeatures.map((feature) => (
          <div 
            key={feature.id} 
            className={`flex justify-center feature-button-wrapper overflow-visible`} 
            style={{ 
              visibility: 'visible',
              display: 'flex'
            }}
          >
            <FeatureButton
              feature={feature}
              isActive={activeFeature === feature.id}
              onClick={() => handleFeatureClick(feature)}
            />
          </div>
        ))}
      </div>
      <style>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        
        /* Mobile: col-4 con scroll cuando hay más de 2 botones */
        @media (max-width: 1023px) {
          .feature-selector-container.has-more-than-two {
            overflow-x: auto;
            scroll-snap-type: x mandatory;
            -webkit-overflow-scrolling: touch;
          }
          
          .feature-selector-container.has-more-than-two .feature-button-wrapper {
            flex: 0 0 33.333%;
            width: 33.333%;
            flex-shrink: 0;
            scroll-snap-align: start;
          }
        }
        
        /* Desktop: comportamiento original */
        @media (min-width: 1024px) {
          .feature-selector-container.has-more-than-two {
            padding-left: 0;
            padding-right: 0;
            overflow-x: visible;
            scroll-snap-type: none;
          }
          
          .feature-selector-container.has-more-than-two .feature-button-wrapper {
            flex-basis: auto;
            width: auto;
            min-width: 130px;
            max-width: 140px;
            flex-shrink: 1;
            scroll-snap-align: none;
          }
        }
      `}</style>
    </>
  );
};

export default FeatureSelector;
